import { LineItem } from 'shared/types/cart';
import {
  ProcessCheckoutPayload,
  PSP,
  UNKNOWN_PSP,
} from 'shared/types/checkout';
import { hasBundleId } from 'helpers/bundleHelper';
import {
  COHESION_BUNDLE_MAPPER,
  CohesionBundleType,
  conversionEvents,
  ConversionTracked,
} from 'helpers/eventing';
import { getProductsFromLineItems } from 'helpers/get-products-from-line-items';
import { centsToDollars, sumMoney } from 'helpers/priceHelpers';
import { sendGTMEvent } from 'helpers/sendGTMEvent';
import { hyphenateForTagular } from 'helpers/tagular';
import {
  GetProcessCheckoutPayload,
  GetValidateCheckoutPayloadForPSPOptions,
  ProcessCheckoutPayloadForPSP,
  SendOrderCompletedEventOptions,
  SendPaymentInfoEnteredEventOptions,
  ValidateCheckoutPayload,
  ValidatePayloadForPSP,
} from './types';

export * from 'helpers/alertCodeHelpers';

export const getValidateCheckoutPayloadForPSP = <T extends PSP>({
  cartID,
  priceDetails,
  psp,
  pspData,
}: GetValidateCheckoutPayloadForPSPOptions<T>): ValidateCheckoutPayload<T> => {
  switch (psp) {
    case PSP.STRIPE:
      return {
        actionName: `${psp}/validateStripeCheckout`,
        payload: {
          cartID,
          amount: priceDetails.cartTotal ?? 0,
          paymentMethodID: pspData,
        } as ValidatePayloadForPSP[T],
      };
    case PSP.PAYPAL:
      return {
        actionName: `${psp}/validatePayPalCheckout`,
        payload: pspData as ValidatePayloadForPSP[T],
      };
    default:
      return {
        actionName: `${psp}/validateStripeCheckout`,
        payload: pspData as ValidatePayloadForPSP[T],
      };
  }
};

export const getProcessCheckoutPayloadForPSP = ({
  ctPaymentID,
  cartID,
  payerName,
  psp,
}: GetProcessCheckoutPayload): ProcessCheckoutPayloadForPSP => {
  const payload = {
    ctPaymentID,
    payerName,
    cartID,
  } as ProcessCheckoutPayload;

  switch (psp) {
    case PSP.STRIPE:
      return {
        actionName: `${psp}/processStripeCheckout`,
        payload,
      };
    case PSP.PAYPAL:
      return {
        actionName: `${psp}/capturePayPalOrder`,
        payload,
      };
    default:
      return {
        actionName: `${psp as string}/processCheckout`,
        payload,
      };
  }
};

export const sendPaymentInfoEnteredEvent = ({
  cartID,
  priceDetails,
  paymentMethodType,
  lineItems,
}: SendPaymentInfoEnteredEventOptions) => {
  sendGTMEvent({
    event: 'Payment Info Entered',
    properties: {
      track_plan_id: 31,
      trigger_source: 'client-side',
      cart_id: cartID,
      checkout_id: cartID,
      currency: priceDetails.currencyCode ?? 'USD',
      revenue: centsToDollars(priceDetails.lineItemsTaxed.amount),
      payment_method: paymentMethodType,
      discount: priceDetails.discountInDollars,
      products: getProductsFromLineItems(lineItems),
    },
  });
};

export const sendOrderCompletedEvent = <T extends PSP | UNKNOWN_PSP>({
  psp,
  cartID,
  priceDetails,
  paymentMethodType,
  order: {
    orderId,
    taxed,
    discountCodes,
    discountOnLineItems,
    discountOnTotalPrice,
    lineItems,
  },
}: SendOrderCompletedEventOptions<T>) => {
  sendGTMEvent({
    event: 'Order Completed',
    properties: {
      track_plan_id: 32,
      trigger_source: 'client-side',
      order_id: orderId,
      checkout_id: cartID,
      total: centsToDollars(taxed?.grossAmount),
      revenue: centsToDollars(priceDetails.lineItemsTaxed.amount),
      currency: priceDetails.currencyCode ?? 'USD',
      tax: centsToDollars(taxed?.totalTax),
      coupon: discountCodes[discountCodes.length - 1]?.code ?? null,
      coupon_name: discountCodes
        .slice(0, -1)
        .map(({ code }) => code)
        .filter(Boolean),
      discount: centsToDollars(
        sumMoney(discountOnTotalPrice, discountOnLineItems),
      ),
      processor_name: psp,
      payment_method: paymentMethodType,
      products: getProductsFromLineItems(lineItems),
    },
  });
};

export const sendCohesionConversionEvent = (
  lineItems: LineItem[],
  category: string,
  action: string,
) => {
  const isBundle = hasBundleId(lineItems);
  const products = lineItems.map(({ variant, name }) => {
    const productId =
      variant?.attributes?.['external-ids-product'] ??
      variant?.attributes?.['course-key'];

    const productType =
      (variant?.attributes?.programProductTypeKey as CohesionBundleType) ??
      'certificate';

    const courseVariant: string | undefined = isBundle
      ? COHESION_BUNDLE_MAPPER[productType]
      : 'courses';

    return {
      id: productId ?? '',
      brand: hyphenateForTagular(variant?.attributes?.['brand-text'] ?? ''),
      externalId: variant?.attributes?.['external-ids-variant'] ?? '',
      category: variant?.attributes?.['primary-subject-area'] ?? '',
      variant: courseVariant,
      name,
    };
  });

  const tagularElement: ConversionTracked['metadata'] = {
    category,
    action,
    timestamp: new Date().toISOString(),
    productList: products,
  };

  const { conversionTracked } = conversionEvents({
    metadata: tagularElement,
  });
  conversionTracked();
};
